import React from 'react';
import './MouvementActionIcon.less';
import type { Exercice, Mouvement } from '@mairistem/comptabilite';
import { Combobox, Icon, type UserRole } from '@mairistem/finances-utils';
import { useHistory } from 'react-router';
import { BordereauWithState } from '../../../../interfaces/pieces/TreeRow';
import BaseLiquidationAction from '../../../../classes/pieces/BaseLiquidationAction';
import MouvementActionType from '../../../../interfaces/pieces/mouvements/liquidation/MouvementActionType';
import TreeviewActionResult from '../../../../interfaces/pieces/TreeviewActionResult';
import handleCheckAction from '../../../../utils/handleCheckAction';
import MouvementModalProvider from './Modals/MouvementModalProvider';
import ActionResultType from '../../../../interfaces/pieces/ActionResultType';

interface ActionIconProps {
  alwaysVisible?: boolean;
  mouvement: Mouvement;
  bordereau: BordereauWithState;
  exercice: Exercice;
  userRole: UserRole;
  onChange: (rowData: Mouvement) => void;
  onDelete?: (rowData: Mouvement) => void;
  onAdd?: (rowData: Mouvement) => void;
}

const MouvementActionIcon = ({
  alwaysVisible = false,
  mouvement,
  bordereau,
  exercice,
  userRole,
  onDelete,
  onChange,
  onAdd,
}: ActionIconProps) => {
  // Permet de forcer l'affichage de la combobox regroupant les actions possibles
  const [forceVisible, setForceVisible] = React.useState<boolean>(alwaysVisible);
  const [
    currentActionMouvement,
    setCurrentActionMouvement,
  ] = React.useState<TreeviewActionResult<Mouvement> | null>(null);

  const history = useHistory();

  const mouvementActions = bordereau.state.getMouvementActions(userRole).filter(
    (action) => (action.isShown(bordereau, mouvement)),
  );

  const handleChange = async (_e: React.BaseSyntheticEvent, { value }) => {
    const actionMouvement: TreeviewActionResult<Mouvement> = mouvementActions.find(
      (a) => a.type === value,
    )?.execute(mouvement, exercice, bordereau);
    // Gestion d'action de redirection avec hook
    if (actionMouvement?.type === ActionResultType.LINK) {
      switch (actionMouvement.extraProps.link.typeAction) {
        case MouvementActionType.SHOW_MOUVEMENT:
        case MouvementActionType.UPDATE_MOUVEMENT:
          history.push(`/liquidations/${mouvement.identifiant}`, { readonly: actionMouvement.extraProps.link.readonly });
          break;
        default:
          break;
      }
    } else {
      await handleCheckAction(
        actionMouvement,
        (updatedMouvement) => updatedMouvement,
        onChange,
        onDelete,
        onAdd,
        setCurrentActionMouvement,
      );
    }
  };

  /**
   * Validation de l'action
   */
  const handleChangeAction = async (mouvementActionType: MouvementActionType, data: any): Promise<void> => {
    // Exécution de l'action en back
    const actionMouvement = mouvementActions.find(
      (a) => a.type === mouvementActionType,
    )?.executeAction({
      ...mouvement,
      ...data,
    }, exercice);

    // Modification de la TreeView en conséquence
    await handleCheckAction(
      actionMouvement,
      (updatedMouvement) => (updatedMouvement),
      onChange,
      onDelete,
      onAdd,
      setCurrentActionMouvement,
    );
  };

  return (
    <div className={`${(alwaysVisible || forceVisible) && 'always-visible'} mouvement-action-icon`}>
      {
        currentActionMouvement && (
          <MouvementModalProvider
            treeViewAction={currentActionMouvement}
            mouvement={mouvement}
            onChange={handleChangeAction}
            exercice={exercice}
            typePes={bordereau?.typePes}
          />
        )
      }
      <Combobox
        options={mouvementActions.map((action: BaseLiquidationAction<Mouvement, MouvementActionType>) => ({
          key: action.label,
          text: action.label,
          value: action.type,
          icon: action.isSuperUser && 'crown',
          'data-testid': `mouvementActionComboboxOption${action.type}`,
        }))}
        icon={null}
        trigger={<Icon name="Menu" iconSet="Lucide" />}
        onChange={handleChange}
        direction="left"
        value=""
        onClose={() => setForceVisible(false)}
        onOpen={() => setForceVisible(true)}
        data-testid="mouvementActionIcon"
      />
    </div>
  );
};

export default MouvementActionIcon;
